import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';

import LoadingSpinner from '../LoadingSpinner.jsx';
import SongAutoSuggest from '../SongAutoSuggest.jsx';
import RequesterAutoSuggest from '../RequesterAutoSuggest.jsx';
import { useT } from '../../../contexts/LocaleContext.jsx';
import { serverMessage } from '../../../_helpers/errorMessage.js';
import '../../../styles/AutoSuggest.css';

const AddToQueueModal = ({ isOpen, onClose, onSave, initialValues }) => {
  const t = useT();
  // Why the last Save failed, shown above the buttons (ENG-504).
  const [saveError, setSaveError] = useState(null);
  const [formValues, setFormValues] = useState({
    Title: '',
    Artist: '',
    RequestedBy: '',
    Position: 0, // Default Position to 0
  });

  const [loading, setLoading] = useState(false);

  // Handle suggestion selection for both title and artist
  // The songs API answers with lowercase title/artist (ENG-506); the
  // capitalised names are kept as a fallback. Picking from either field
  // fills both with the song's values.
  const handleSongSelect = (song) => {
    setFormValues(prev => ({
      ...prev,
      Title: song?.title ?? song?.Title ?? prev.Title,
      Artist: song?.artist ?? song?.Artist ?? prev.Artist,
    }));
  };

  // Reset the form each time the modal opens, from the initialValues it opens
  // with. Not on every initialValues change: a parent that re-renders while
  // the modal is open (the dashboard does, every second while live, and on
  // each queue update) would otherwise wipe what's being typed (ENG-461).
  useEffect(() => {
    if (!isOpen) return;
    if (initialValues) {
      setFormValues({
        Title: initialValues.Title || '',
        Artist: initialValues.Artist || '',
        RequestedBy: initialValues.RequestedBy || '',
        Position: initialValues.Position || 0,
      });
    } else {
      // Reset to default values when no initial values are provided
      setFormValues({
        Title: '',
        Artist: '',
        RequestedBy: '',
        Position: 0,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- read at open, deliberately
  }, [isOpen]);

  const handleTitleChange = (value) => {
    setFormValues((prev) => ({ ...prev, Title: value }));
  };

  const handleArtistChange = (value) => {
    setFormValues((prev) => ({ ...prev, Artist: value }));
  };

  const handleRequesterChange = (value) => {
    setFormValues((prev) => ({ ...prev, RequestedBy: value }));
  };

  const handleSubmit = async () => {
    if (loading) return; // Prevent multiple submissions
    
    setLoading(true);
    setSaveError(null);
    try {
      await onSave(formValues); // Wait for the save operation to complete
    } catch (error) {
      // onSave rejects when the song couldn't be added (ENG-504): say why,
      // and leave the form filled in with Save available again.
      console.error('Error saving song:', error);
      setSaveError(serverMessage(error) || t('streamer.addToQueue.saveFailed'));
    } finally {
      setLoading(false); // Reset loading state after the operation completes
    }
  };

  const handleClose = () => {
    setSaveError(null);
    setFormValues({
      Title: '',
      Artist: '',
      RequestedBy: '',
      Position: 0, // Reset Position to 0
    });
    
    onClose();
    setLoading(false); // Reset loading state when closing the modal
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel={t('streamer.addToQueue.contentLabel')}
      className="modalContent" overlayClassName="modalOverlay"
    >
      <h2>{t('streamer.addToQueue.heading')}</h2>
      <form>
        <input type="hidden" name="Position" value={formValues.Position} />
        <div className="form-group">
          <label htmlFor="title-input">{t('streamer.addToQueue.title')}</label>
          <SongAutoSuggest
            id="title-input"
            testId="add-to-queue-title"
            placeholder={t('streamer.addToQueue.title')}
            value={formValues.Title || ''}
            onChange={handleTitleChange}
            onSongSelect={handleSongSelect}
            className="form-control"
          />
        </div>
        <div className="form-group">
          <label htmlFor="artist-input">{t('streamer.addToQueue.artist')}</label>
          <SongAutoSuggest
            id="artist-input"
            testId="add-to-queue-artist"
            placeholder={t('streamer.addToQueue.artist')}
            value={formValues.Artist || ''}
            onChange={handleArtistChange}
            onSongSelect={handleSongSelect}
            className="form-control"
          />
        </div>
        <div className="form-group">
          <label htmlFor="requested-by-input">{t('streamer.addToQueue.requestedBy')}</label>
          <RequesterAutoSuggest
            id="requested-by-input"
            value={formValues.RequestedBy || ''}
            onChange={handleRequesterChange}
            onSelect={handleRequesterChange}
            placeholder={t('streamer.addToQueue.requestedBy')}
            className="form-control"
          />
        </div>

        {saveError && (
          <p className="modal-error" role="alert">{saveError}</p>
        )}

        {loading ? (
          <LoadingSpinner />
        ) : (
          <div className="button-group">
            <button type="button" onClick={handleSubmit} className="btn-save" data-testid="add-to-queue-save">{t('common.actions.save')}</button>
            <button type="button" onClick={handleClose} className="btn-cancel">{t('common.actions.cancel')}</button>
          </div>
        )}
        
      </form>
    </Modal>
  );
};

export default AddToQueueModal;
